import React, { useEffect, useRef, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { api } from '../lib/api'
import { track } from '../lib/analytics'
import { sortProgrammesByUniversityThenPriority } from '../lib/sortProgrammes'
import { openPricing, PAID_TIERS_ENABLED } from '../lib/payments'

/**
 * ActiveContextSwitcher - the "Praticar para: <programme> · <uni> ▾" dropdown
 * at the top of /subjects. Drives which (university, programme) the student
 * is currently practicing for.
 *
 * Data source: /subjects response already echoes `student_programmes`; this
 * component receives that list as a prop + the current active_programme_id
 * from /auth/me.
 *
 * Actions:
 *   - Click an entry     → POST /auth/switch-context
 *   - Remove (× icon)    → POST /auth/remove-programme (with confirm)
 *   - + Adicionar curso  → /universities (reuse pick flow); tier cap respected
 *                         server-side by commit-programme
 */

export interface SwitcherProgramme {
  id:       string
  priority: number
  period?:  string                                    // "laboral" | "pos_laboral" | "ead"
  programme: {
    id:                     string
    name:                   string
    university:             string
    university_max_options?: number                   // from University.max_options_per_candidate
    is_placeholder:         boolean
    periods_available?:     string[]
  }
}

function periodLabel(period?: string): string {
  if (period === 'pos_laboral') return 'Pós-Laboral'
  if (period === 'laboral')     return 'Laboral'
  if (period === 'ead')         return 'EaD'
  return ''
}

interface Props {
  programmes:         SwitcherProgramme[]
  activeProgrammeId:  string | null | undefined
  maxProgrammes:      number | null | undefined       // null = unlimited
  maxUniversities:    number | null | undefined       // null = unlimited
  onChanged:          () => void                       // parent refetches /subjects + /auth/me
}

export function ActiveContextSwitcher({ programmes, activeProgrammeId, maxProgrammes, maxUniversities, onChanged }: Props) {
  const navigate = useNavigate()
  const [open, setOpen]       = useState(false)
  const [busy, setBusy]       = useState(false)
  const [error, setError]     = useState<string | null>(null)
  const rootRef = useRef<HTMLDivElement>(null)

  // Close dropdown on outside click
  useEffect(() => {
    if (!open) return
    const h = (e: MouseEvent) => {
      if (rootRef.current && !rootRef.current.contains(e.target as Node)) setOpen(false)
    }
    document.addEventListener('mousedown', h)
    return () => document.removeEventListener('mousedown', h)
  }, [open])

  const active = programmes.find(sp => sp.programme.id === activeProgrammeId) ?? programmes[0]
  if (!active) return null  // shouldn't happen in v2 (every student has ≥1 commitment)

  const switchTo = async (programmeId: string) => {
    if (programmeId === activeProgrammeId) { setOpen(false); return }
    setBusy(true); setError(null)
    try {
      await api.post('/auth/switch-context', { programme_id: programmeId })
      const target = programmes.find(sp => sp.programme.id === programmeId)
      if (target) {
        track('subject_switched', {
          from_programme_id: activeProgrammeId ?? null,
          to_programme_id:   programmeId,
          from_university:   active.programme.university,
          to_university:     target.programme.university,
          cross_group:       active.programme.university !== target.programme.university,
        })
      }
      setOpen(false)
      onChanged()
    } catch (err: any) {
      setError(err?.data?.error ?? err?.message ?? 'Erro ao mudar de curso')
    } finally {
      setBusy(false)
    }
  }

  const remove = async (sp: SwitcherProgramme) => {
    const label = `${sp.programme.name} (${sp.programme.university})`
    if (!confirm(`Remover "${label}" da tua lista de cursos?`)) return
    setBusy(true); setError(null)
    try {
      await api.post('/auth/remove-programme', { programme_id: sp.programme.id })
      onChanged()
    } catch (err: any) {
      setError(err?.data?.error ?? err?.message ?? 'Erro ao remover')
    } finally {
      setBusy(false)
    }
  }

  // Slot-usage derived values. Two independent caps interact:
  //   - Per-university: the student can't exceed University.max_options_per_candidate
  //     at a single uni (e.g. UEM allows 2, UJC allows 1).
  //   - Tier-level: the subscription caps total programmes / universities.
  const realProgrammes = programmes.filter(p => !p.programme.is_placeholder)
  const usedProgrammes = realProgrammes.length
  const usedUnis       = new Set(realProgrammes.map(p => p.programme.university)).size
  const programmesFull = maxProgrammes !== null && maxProgrammes !== undefined && usedProgrammes >= maxProgrammes
  const universitiesFull = maxUniversities !== null && maxUniversities !== undefined && usedUnis >= maxUniversities

  // Per-uni count + rule for the active university
  const activeUniMax = active.programme.university_max_options ?? 1
  const sameUniCount = realProgrammes.filter(p => p.programme.university === active.programme.university).length
  const sameUniFull  = sameUniCount >= activeUniMax

  const addAnotherCourse = () => {
    // If the tier caps allow more, go to /universities picker (or straight to current uni's /courses)
    if (programmesFull) return
    setOpen(false)
    navigate('/universities?add=1')
  }

  const addAnotherAtSameUni = () => {
    if (programmesFull) return
    // Per-uni cap reached - keep the dropdown open so the × to remove an
    // existing commitment is one tap away. No blocking alert, no wall of text.
    if (sameUniFull) return
    setOpen(false)
    navigate(`/courses/${encodeURIComponent(active.programme.university)}`)
  }

  const showPeriod = !active.programme.is_placeholder && (active.programme.periods_available?.length ?? 0) > 1
  const activeLabel = active.programme.is_placeholder
    ? `A decidir · ${active.programme.university}`
    : showPeriod
      ? `${active.programme.name} · ${active.programme.university} · ${periodLabel(active.period)}`
      : `${active.programme.name} · ${active.programme.university}`

  return (
    <div ref={rootRef} className="relative inline-block">
      <button
        onClick={() => setOpen(o => !o)}
        disabled={busy}
        className="flex items-center gap-2 text-sm bg-white border border-gray-200 rounded-lg px-3 py-1.5 hover:border-indigo-300 disabled:opacity-60"
      >
        <span className="text-[10px] text-gray-400 uppercase tracking-wide font-semibold">Praticar para</span>
        <span className="font-semibold text-gray-800 truncate max-w-[280px]">{activeLabel}</span>
        <span className="text-gray-400">▾</span>
      </button>

      {open && (
        <div className="absolute z-30 mt-1 left-0 w-80 bg-white border border-gray-100 shadow-lg rounded-xl overflow-hidden">
          {error && <p className="text-xs bg-red-50 text-red-700 px-3 py-2 border-b border-red-100">{error}</p>}
          <ul className="max-h-[60vh] overflow-y-auto">
            {sortProgrammesByUniversityThenPriority(programmes
              // Hide placeholders at universities where the student already has a real commitment -
              // they're just data leftovers; confusing to render a "A decidir" alongside real courses.
              .filter(sp => !sp.programme.is_placeholder || !realProgrammes.some(rp => rp.programme.university === sp.programme.university))
            ).map(sp => {
              const isActive = sp.programme.id === activeProgrammeId
              return (
                <li key={sp.id} className={`border-b border-gray-50 last:border-0 ${isActive ? 'bg-indigo-50/40' : ''}`}>
                  <div className="flex items-center justify-between gap-2 px-3 py-2">
                    <button
                      onClick={() => switchTo(sp.programme.id)}
                      disabled={busy}
                      className="flex-1 text-left text-sm disabled:opacity-40"
                    >
                      <div className="flex items-center gap-2">
                        <span className="text-[10px] bg-gray-100 text-gray-600 px-1.5 py-0.5 rounded font-semibold shrink-0">
                          {sp.priority}ª
                        </span>
                        <span className={`truncate ${isActive ? 'font-semibold text-indigo-700' : 'text-gray-700'}`}>
                          {sp.programme.is_placeholder ? 'A decidir' : sp.programme.name}
                        </span>
                      </div>
                      <div className="text-[10px] text-gray-400 ml-7">
                        {sp.programme.university}
                        {sp.programme.is_placeholder && ' · só praticar'}
                        {(sp.programme.periods_available?.length ?? 0) > 1 && !sp.programme.is_placeholder && ` · ${periodLabel(sp.period)}`}
                      </div>
                    </button>
                    <button
                      onClick={() => remove(sp)}
                      disabled={busy}
                      className="text-gray-300 hover:text-red-500 text-sm px-1"
                      title="Remover da lista"
                    >
                      ×
                    </button>
                  </div>
                </li>
              )
            })}
          </ul>

          <div className="p-2 bg-gray-50/60 border-t border-gray-100 space-y-1">
            <button
              onClick={addAnotherAtSameUni}
              disabled={programmesFull || sameUniFull}
              title={sameUniFull ? `Remove um dos cursos actuais com × para poderes escolher outro.` : undefined}
              className={`w-full text-left text-xs px-3 py-1.5 rounded-lg ${
                sameUniFull
                  ? 'text-amber-700 cursor-default'
                  : 'text-indigo-700 hover:bg-indigo-50'
              } disabled:text-gray-300 disabled:hover:bg-transparent`}
            >
              {sameUniFull
                ? `Clicar × para trocar cursos em ${active.programme.university}`
                : `+ Adicionar outro curso em ${active.programme.university}`}
              {programmesFull && !sameUniFull && ' (plano esgotado)'}
            </button>
            <button
              onClick={addAnotherCourse}
              disabled={programmesFull || universitiesFull}
              className="w-full text-left text-xs text-indigo-700 hover:bg-indigo-50 disabled:text-gray-300 disabled:hover:bg-transparent px-3 py-1.5 rounded-lg"
            >
              + Adicionar curso noutra universidade
              {programmesFull ? ' (plano esgotado)' : universitiesFull ? ' (limite de universidades atingido)' : ''}
            </button>
            {PAID_TIERS_ENABLED && (programmesFull || universitiesFull) && (
              <button
                onClick={() => { setOpen(false); openPricing() }}
                className="w-full text-center text-xs bg-indigo-600 text-white font-semibold px-3 py-1.5 rounded-lg hover:bg-indigo-700"
              >
                Ver planos para desbloquear
              </button>
            )}
          </div>
        </div>
      )}
    </div>
  )
}
